import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  Psychology as PsychologyIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import {
  createAbcPattern,
  deleteAbcPatternById,
  getAbcPattern,
} from "../../../../../../api/services/observations";
import { enqueueSnackbar } from "notistack";
import { QK } from "../../../../../../base/qk";
import queryClient from "../../../../../../base/QueryClient";
import { getAllLocations } from "../../../../../../api/services/settings";
import { getSubLocations } from "../../../../../../api/services/scheduling";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import CustomInput from "../../../../../FormFields/CustomInput/CustomInput";
import CustomSelect from "../../../../../FormFields/CustomSelect/CustomSelect";
import CustomDateTime from "../../../../../CustomForm/CustomDateTime";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import DataTable from "../../../../../Table/DataTable";
import { EditAbcPatternModal } from "./EditAbcPatternModal";
import {
  BEHAVIOUR_TYPES,
  RISK_LEVELS,
  SERVICE_USER_PRESENTATION,
} from "./abcpattern.config";

const getNowDateTime = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const AbcPatterns = () => {
  const theme = useTheme();
  const { id: clientId } = useParams();
  const [formData, setFormData] = useState({
    date_time: getNowDateTime(),
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAbcPattern, setSelectedAbcPattern] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const {
    data: abcPatterns,
    refetch,
    isLoading,
  } = useQuery([QK.abcPattern, clientId], () => getAbcPattern(clientId));

  const { data: locations } = useQuery([QK.locations], getAllLocations);

  const { data: sublocations } = useQuery(
    [QK.subLocations, formData.location],
    () => getSubLocations(formData.location),
    { enabled: !!formData.location }
  );

  const abcPatternPostMutation = useMutation(createAbcPattern, {
    onSuccess: () => {
      enqueueSnackbar("ABC Pattern created successfully", {
        variant: "success",
      });
      refetch();
      resetForm();
    },
    onError: () =>
      enqueueSnackbar("Failed to create ABC Pattern", { variant: "error" }),
  });

  const deleteMutation = useMutation(deleteAbcPatternById, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.abcPattern]);
      enqueueSnackbar("ABC Pattern deleted successfully", {
        variant: "success",
      });
      refetch();
    },
    onError: () =>
      enqueueSnackbar("Failed to delete ABC Pattern", { variant: "error" }),
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLocationChange = (e) => {
    const locationId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      location: locationId,
      area: "",
    }));
  };

  const handleRiskLevelSelect = (level) => {
    setFormData((prev) => ({
      ...prev,
      risk_level: level,
    }));
  };

  const resetForm = () => setFormData({ date_time: getNowDateTime() });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      client: clientId,
      date_time: formData.date_time,
    };
    abcPatternPostMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (selectedAbcPattern) {
      deleteMutation.mutate(selectedAbcPattern);
    }
    setShowConfirmModal(false);
    setSelectedAbcPattern(null);
  };

  const headers = [
    { key: "date_time", label: "Date" },
    { key: "behaviour", label: "Behaviour" },
    { key: "risk_level", label: "Risk" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "date_time") {
      return item.date_time ? new Date(item.date_time).toLocaleString() : "-";
    }
    if (header.key === "behaviour") {
      return (
        <Typography variant="body2" fontWeight={500}>
          {item.behaviour || "-"}
        </Typography>
      );
    }
    if (header.key === "risk_level") {
      const colorMap = {
        LOW: "success",
        MEDIUM: "warning",
        HIGH: "info",
        CRITICAL: "error",
      };
      return (
        <Chip
          label={item.risk_level || "N/A"}
          size="small"
          color={colorMap[item.risk_level] || "default"}
          sx={{
            fontWeight: 600,
            minWidth: 60,
            height: 24,
            fontSize: "0.7rem",
          }}
        />
      );
    }
    return item[header.key] || "-";
  };

  const locationOptions = (locations || []).map((loc) => ({
    value: loc.id,
    label: loc.name,
  }));

  const areaOptions = (sublocations || []).map((sub) => ({
    value: sub.id,
    label: sub.name,
  }));

  const behaviourTypeOptions = BEHAVIOUR_TYPES.map((opt) => ({
    value: opt.value,
    label: opt.name,
  }));

  const presentationOptions = SERVICE_USER_PRESENTATION.map((opt) => ({
    value: opt.value,
    label: opt.name,
  }));

  return (
    <Box sx={{ p: 1.5 }}>
      <Card
        sx={{
          borderRadius: "14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          overflow: "hidden",
          mb: 2.5,
          border: "1px solid #EEF1F5",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            background: "linear-gradient(135deg, #FAFBFC 0%, #F4F7FB 100%)",
            borderBottom: "1px solid #EEF1F5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "10px",
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.14
                )} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <PsychologyIcon
                sx={{ color: theme.palette.primary.main, fontSize: 18 }}
              />
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: "#1A1A2E", lineHeight: 1.2 }}
              >
                Record ABC Pattern
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#7A8699", fontSize: "0.68rem" }}
              >
                Capture behaviour observations
              </Typography>
            </Box>
          </Box>
          <Chip
            label="New"
            size="small"
            sx={{
              height: 22,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: "#D1FAE5",
              color: "#039855",
              borderRadius: "6px",
            }}
          />
        </Box>

        <CardContent sx={{ p: 2.5 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={1.75}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomDateTime
                  name="date_time"
                  label="Date / Time *"
                  value={formData.date_time || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomSelect
                  name="location"
                  label="Location *"
                  value={formData.location || ""}
                  onChange={handleLocationChange}
                  options={locationOptions}
                  placeholder="Select location"
                  required
                  fullWidth
                  size="small"
                  searchable={true}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomSelect
                  name="area"
                  label="Area *"
                  value={formData.area || ""}
                  onChange={handleChange}
                  options={areaOptions}
                  placeholder="Select area"
                  required
                  fullWidth
                  size="small"
                  searchable={true}
                  disabled={!formData.location}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomSelect
                  name="behaviour_type"
                  label="Behaviour Type *"
                  value={formData.behaviour_type || ""}
                  onChange={handleChange}
                  options={behaviourTypeOptions}
                  placeholder="Select behaviour type"
                  required
                  fullWidth
                  size="small"
                  searchable={true}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomInput
                  type="text"
                  name="antecedent"
                  label="Antecedent *"
                  value={formData.antecedent || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomInput
                  type="text"
                  name="behaviour"
                  label="Behaviour *"
                  value={formData.behaviour || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomInput
                  type="text"
                  name="who_else_present"
                  label="Who else present?"
                  value={formData.who_else_present || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomInput
                  type="text"
                  name="consequences"
                  label="Consequences *"
                  value={formData.consequences || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomInput
                  type="text"
                  name="what_they_said"
                  label="What they said?"
                  value={formData.what_they_said || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomInput
                  type="text"
                  name="action_taken"
                  label="Action Taken *"
                  value={formData.action_taken || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomInput
                  type="number"
                  name="behaviour_duration"
                  label="Behaviour Duration (mins)"
                  value={formData.behaviour_duration || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomInput
                  type="number"
                  name="situation_duration"
                  label="Situation Duration (mins)"
                  value={formData.situation_duration || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomInput
                  type="text"
                  name="other_appearance_details"
                  label="Other appearance"
                  value={formData.other_appearance_details || ""}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <CustomSelect
                  name="service_user_presentation"
                  label="Service User Presentation"
                  value={formData.service_user_presentation || ""}
                  onChange={handleChange}
                  options={presentationOptions}
                  placeholder="Select presentation"
                  fullWidth
                  size="small"
                  searchable={true}
                />
              </Grid>

              <Grid size={12}>
                <Typography
                  sx={{
                    mt: 0.75,
                    mb: 0.75,
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    color: "#475467",
                  }}
                >
                  Risk Level
                </Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {RISK_LEVELS.map(({ value, color }) => (
                    <Button
                      key={value}
                      variant={
                        formData.risk_level === value ? "contained" : "outlined"
                      }
                      color={color}
                      size="small"
                      sx={{
                        borderRadius: "8px",
                        minWidth: 64,
                        height: 28,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        textTransform: "none",
                      }}
                      onClick={() => handleRiskLevelSelect(value)}
                    >
                      {value}
                    </Button>
                  ))}
                </Box>
              </Grid>

              <Grid size={12}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    alignItems: "center",
                    px: 1.25,
                    py: 0.75,
                    borderRadius: "10px",
                    border: "1px solid #EEF1F5",
                    bgcolor: "#FAFBFC",
                  }}
                >
                  <CustomCheckbox
                    label="Share with Manager"
                    name="manager"
                    checked={formData.manager || false}
                    onClick={(checked) =>
                      handleChange({
                        target: {
                          name: "manager",
                          type: "checkbox",
                          checked,
                        },
                      })
                    }
                  />
                  <CustomCheckbox
                    label="Handover"
                    name="handover"
                    checked={formData.handover || false}
                    onClick={(checked) =>
                      handleChange({
                        target: {
                          name: "handover",
                          type: "checkbox",
                          checked,
                        },
                      })
                    }
                  />
                </Box>
              </Grid>

              <Grid size={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1.25,
                    pt: 1.25,
                    mt: 0.25,
                    borderTop: "1px dashed #E5E9F0",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={abcPatternPostMutation.isLoading}
                    startIcon={<ClearIcon fontSize="small" />}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      borderRadius: "8px",
                      borderColor: "#D0D5DD",
                      color: "#6B7280",
                      px: 2.5,
                      py: 0.75,
                      fontSize: "0.8rem",
                      "&:hover": {
                        bgcolor: "#F9FAFB",
                        borderColor: "#9CA3AF",
                      },
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={abcPatternPostMutation.isLoading}
                    startIcon={
                      abcPatternPostMutation.isLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        <SaveIcon fontSize="small" />
                      )
                    }
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      bgcolor: "#2469F0",
                      px: 3,
                      py: 0.75,
                      fontSize: "0.8rem",
                      "&:hover": {
                        bgcolor: "#1A5BD4",
                        boxShadow: "0 4px 12px rgba(36, 105, 240, 0.25)",
                      },
                    }}
                  >
                    {abcPatternPostMutation.isLoading ? "Saving..." : "Save"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: "14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          overflow: "hidden",
          border: "1px solid #EEF1F5",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            background: "linear-gradient(135deg, #FAFBFC 0%, #F4F7FB 100%)",
            borderBottom: "1px solid #EEF1F5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: "#1A1A2E" }}
            >
              ABC Pattern Records
            </Typography>
            <Chip
              label={abcPatterns?.length || 0}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 600,
                bgcolor: "#F3F4F6",
                color: "#475467",
                borderRadius: "6px",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={abcPatterns || []}
            headers={headers}
            onUpdate={(id) => {
              setSelectedAbcPattern(id);
              setShowEditModal(true);
            }}
            onDelete={(id) => {
              setSelectedAbcPattern(id);
              setShowConfirmModal(true);
            }}
            canUpdate={true}
            canDelete={true}
            renderCell={renderCell}
            loading={isLoading}
            size="small"
          />
        </CardContent>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleDelete}
        title="Delete Record"
        body="Are you sure you want to delete this ABC Pattern record?"
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <EditAbcPatternModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        selectedAbcPattern={selectedAbcPattern}
        refetch={refetch}
      />
    </Box>
  );
};

export default AbcPatterns;